export class Popup {
  constructor(selector = "", settings = {}) {
    if (!selector) return;
    this.selector = selector;
    this.element = document.querySelector(selector);
    if (!this.element) return;

    this.canAction = true;
    this.settings = settings;
    this.on = settings.on || {};
    this.animations = null;
    this.previousActiveElement = null;
    this.inertElements = [];

    this._setupEventListeners();
  }

  _setupEventListeners() {
    // 1. Close on background click
    this.element.addEventListener("click", (e) => {
      if (e.target === this.element) this.close();
    });

    // 2. Close buttons
    const closeBtnSelector = this.settings.closeButton || ".close-button";
    this.element.querySelectorAll(closeBtnSelector).forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // 3. Open buttons (using event delegation to avoid querying on init)
    const openBtnSelector = this.settings.openButtons;
    if (openBtnSelector) {
      document.addEventListener("click", (e) => {
        const btn = e.target.closest(openBtnSelector);
        if (btn) {
          e.preventDefault();
          this.open();
        }
      });
    }

    // 4. Resize handling
    if (this.settings.closeOnResize) {
      let resizeTimer;
      window.addEventListener("resize", () => {
        if (!this.element.hasAttribute("open")) return;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.close(), 200);
      });
    }

    this.element.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === "Tab") this._trapFocus(event);
    });

    if (this.on.init) this.on.init.call(this);
  }

  _getFocusableElements() {
    return [...this.element.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter(element => (
      !element.hidden
      && element.getAttribute("aria-hidden") !== "true"
      && !element.closest('details:not([open])')
    ));
  }

  _trapFocus(event) {
    const focusableElements = this._getFocusableElements();
    if (!focusableElements.length) {
      event.preventDefault();
      this.element.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  _setPageInert(isInert) {
    if (isInert) {
      const inertElements = new Set();
      let currentElement = this.element;

      while (currentElement?.parentElement) {
        [...currentElement.parentElement.children]
          .filter(element => element !== currentElement)
          .forEach(element => inertElements.add(element));

        currentElement = currentElement.parentElement;
      }

      this.inertElements = [...inertElements];
      this.inertElements.forEach(element => { element.inert = true; });
      return;
    }

    this.inertElements.forEach(element => { element.inert = false; });
    this.inertElements = [];
  }

  ensureAnimations() {
    if (!this.animations) {
      const styles = getComputedStyle(this.element);
      const parse = (val) => val.endsWith("ms") ? parseFloat(val) : (val.endsWith("s") ? parseFloat(val) * 1000 : 500);
      this.animations = {
        open: parse(styles.getPropertyValue("--_open").trim() || "500ms"),
        close: parse(styles.getPropertyValue("--_close").trim() || "500ms")
      };
    }
  }

  open() {
    if (!this.canAction) return;
    this.ensureAnimations();
    this.canAction = false;
    this.previousActiveElement = document.activeElement;
    this.element.removeAttribute("close");
    this.element.setAttribute("open", "");
    this.element.setAttribute("aria-hidden", "false");
    this._setPageInert(true);
    const firstFocusableElement = this._getFocusableElements()[0];
    setTimeout(() => (firstFocusableElement || this.element).focus(), 50);
    setTimeout(() => { this.canAction = true; }, this.animations.open);
    if (this.on.open) this.on.open.call(this);
  }

  close() {
    if (!this.element.hasAttribute("open")) return;
    this.ensureAnimations();
    this.canAction = false;
    this.element.setAttribute("close", "");
    this.element.removeAttribute("open");
    this.element.setAttribute("aria-hidden", "true");
    this._setPageInert(false);
    if (this.previousActiveElement instanceof HTMLElement) this.previousActiveElement.focus();
    setTimeout(() => { this.canAction = true; }, this.animations.close);
    if (this.on.close) this.on.close.call(this);
  }
}
